import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar, MapPin, Users, Download, Plus, Edit, ExternalLink, Crown, DollarSign, Ticket, Mail, Target, Send, Globe } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, loading: authLoading, isPremium, isBusinessPremium } = useAuth();
  const [myEvents, setMyEvents] = useState([]);
  const [myRsvps, setMyRsvps] = useState([]);
  const [selectedEventAttendees, setSelectedEventAttendees] = useState({ rsvps: [], tickets: [], total: 0 });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-events');
  
  // Invitation state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEvent, setInviteEvent] = useState(null);
  const [jobFunctions, setJobFunctions] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [inviteTargeting, setInviteTargeting] = useState({
    target_job_functions: [],
    target_company_types: [],
    exclude_company_types: [],
    custom_message: ''
  });
  const [invitePreview, setInvitePreview] = useState(null);
  const [sendingInvites, setSendingInvites] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchReferenceData();
    
    const eventId = searchParams.get('event');
    if (eventId) {
      setActiveTab('attendees');
    }
  }, [user, navigate, searchParams, authLoading]);

  const fetchData = async () => {
    try {
      const [eventsRes, rsvpsRes] = await Promise.all([
        axios.get(`${API}/my-events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/my-rsvps`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setMyEvents(eventsRes.data);
      setMyRsvps(rsvpsRes.data);

      const eventId = searchParams.get('event');
      if (eventId) {
        const event = eventsRes.data.find(e => e.id === eventId);
        if (event) {
          fetchEventAttendees(event);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [jobsRes, typesRes] = await Promise.all([
        axios.get(`${API}/job-functions`),
        axios.get(`${API}/company-types`),
      ]);
      setJobFunctions(jobsRes.data);
      setCompanyTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const fetchEventAttendees = async (event) => {
    try {
      const response = await axios.get(`${API}/events/${event.id}/attendees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(event);
      setSelectedEventAttendees(response.data);
    } catch (error) {
      toast.error('Error fetching attendees');
    }
  };

  const downloadCsv = async (event) => {
    try {
      const response = await axios.get(`${API}/events/${event.id}/rsvps/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendees_${event.title.replace(/\s+/g, '_')}_${event.date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV downloaded successfully');
    } catch (error) {
      toast.error('Error downloading CSV');
    }
  };

  const openInviteDialog = (event) => {
    setInviteEvent(event);
    setInviteTargeting({
      target_job_functions: [],
      target_company_types: [],
      exclude_company_types: [],
      custom_message: ''
    });
    setInvitePreview(null);
    setInviteDialogOpen(true);
  };

  const handleTargetingChange = (field, value, checked) => {
    setInviteTargeting(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(v => v !== value)
    }));
    setInvitePreview(null);
  };

  const previewInvitations = async () => {
    if (!inviteEvent) return;
    
    try {
      const params = new URLSearchParams();
      if (inviteTargeting.target_job_functions.length > 0) {
        params.append('target_job_functions', inviteTargeting.target_job_functions.join(','));
      }
      if (inviteTargeting.target_company_types.length > 0) {
        params.append('target_company_types', inviteTargeting.target_company_types.join(','));
      }
      if (inviteTargeting.exclude_company_types.length > 0) {
        params.append('exclude_company_types', inviteTargeting.exclude_company_types.join(','));
      }
      
      const response = await axios.get(
        `${API}/events/${inviteEvent.id}/invite/preview?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitePreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error previewing invitations');
    }
  };

  const sendInvitations = async () => {
    if (!inviteEvent || !invitePreview || invitePreview.matching_members === 0) return;
    
    setSendingInvites(true);
    try {
      const response = await axios.post(
        `${API}/events/${inviteEvent.id}/invite`,
        inviteTargeting,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setInviteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error sending invitations');
    } finally {
      setSendingInvites(false);
    }
  };

  const getCategoryLabel = (value) => {
    const categories = {
      conference: 'Conference',
      workshop: 'Workshop',
      networking: 'Networking Mixer',
      webinar: 'Webinar',
      seminar: 'Seminar',
      training: 'Training Session',
      panel: 'Panel Discussion',
      other: 'Other',
    };
    return categories[value] || value;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <p className="overline text-gold mb-4">Dashboard</p>
            <h1 className="text-3xl md:text-4xl flex items-center gap-3">
              Welcome, {user.full_name}
              {isPremium() && <Crown className="w-6 h-6 text-gold" />}
            </h1>
          </div>
          <div className="flex gap-3 mt-6 md:mt-0">
            {isBusinessPremium() && (
              <Button 
                onClick={() => navigate('/import-event')}
                variant="outline"
                className="uppercase tracking-widest px-4 py-3 text-sm font-semibold rounded-sm border-gold text-gold hover:bg-gold/10"
                data-testid="import-event-btn"
              >
                <Globe className="w-4 h-4 mr-2" /> Import
              </Button>
            )}
            <Button 
              onClick={() => navigate('/create-event')}
              className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-6 py-3 text-sm font-semibold rounded-sm"
              data-testid="dashboard-create-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Event
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-secondary/30 p-1 rounded-sm">
            <TabsTrigger 
              value="my-events" 
              className="rounded-sm data-[state=active]:bg-card data-[state=active]:text-gold uppercase tracking-wider text-sm"
              data-testid="my-events-tab"
            >
              My Events ({myEvents.length})
            </TabsTrigger>
            <TabsTrigger 
              value="my-registrations" 
              className="rounded-sm data-[state=active]:bg-card data-[state=active]:text-gold uppercase tracking-wider text-sm"
              data-testid="my-registrations-tab"
            >
              My Registrations ({myRsvps.length})
            </TabsTrigger>
            <TabsTrigger 
              value="attendees" 
              className="rounded-sm data-[state=active]:bg-card data-[state=active]:text-gold uppercase tracking-wider text-sm"
              data-testid="event-attendees-tab"
            >
              Event Attendees
            </TabsTrigger>
          </TabsList>

          {/* My Events Tab */}
          <TabsContent value="my-events" className="space-y-6">
            {myEvents.length === 0 ? (
              <div className="text-center py-16 border border-border/50 bg-card">
                <Calendar className="w-12 h-12 text-gold mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-xl mb-2">No Events Created</h3>
                <p className="text-muted-foreground mb-6">Start by creating your first event.</p>
                <Button 
                  onClick={() => navigate('/create-event')}
                  className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-6 py-3 text-sm font-semibold rounded-sm"
                  data-testid="create-first-event-dashboard-btn"
                >
                  Create Event
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {myEvents.map((event, index) => (
                  <div 
                    key={event.id} 
                    className="border border-border/50 bg-card p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
                    data-testid={`my-event-${index}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="badge-category">
                          {getCategoryLabel(event.category)}
                        </span>
                        {event.is_paid && (
                          <span className="inline-flex items-center gap-1 text-xs text-gold">
                            <DollarSign className="w-3 h-3" /> ${event.ticket_price}
                          </span>
                        )}
                        {event.is_featured && (
                          <span className="inline-flex items-center gap-1 text-xs text-gold">
                            <Crown className="w-3 h-3" /> Featured
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl mb-2">{event.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(parseISO(event.date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {event.rsvp_count} Attendee{event.rsvp_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/events/${event.id}`)}
                        className="rounded-sm"
                        data-testid={`view-event-btn-${index}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/edit-event/${event.id}`)}
                        className="rounded-sm"
                        data-testid={`edit-event-btn-${index}`}
                      >
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          fetchEventAttendees(event);
                          setActiveTab('attendees');
                        }}
                        className="rounded-sm"
                        data-testid={`manage-attendees-btn-${index}`}
                      >
                        <Users className="w-4 h-4 mr-1" /> Attendees
                      </Button>
                      {isBusinessPremium() && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openInviteDialog(event)}
                          className="rounded-sm text-gold border-gold hover:bg-gold hover:text-primary"
                          data-testid={`invite-members-btn-${index}`}
                        >
                          <Mail className="w-4 h-4 mr-1" /> Invite
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => downloadCsv(event)}
                        className="rounded-sm"
                        data-testid={`download-csv-btn-${index}`}
                      >
                        <Download className="w-4 h-4 mr-1" /> CSV
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {!isBusinessPremium() && myEvents.length > 0 && (
              <div className="border border-gold/50 bg-gold/5 p-6 text-center">
                <Crown className="w-8 h-8 text-gold mx-auto mb-3" />
                <h3 className="text-lg mb-2">Upgrade to Business Premium</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Send targeted invitations to members matching your event criteria.
                </p>
                <Button 
                  onClick={() => navigate('/membership')}
                  className="bg-gold text-primary hover:bg-gold/90 rounded-sm uppercase tracking-widest"
                >
                  Upgrade for $15/mo
                </Button>
              </div>
            )}
          </TabsContent>

          {/* My Registrations Tab */}
          <TabsContent value="my-registrations" className="space-y-6">
            {myRsvps.length === 0 ? (
              <div className="text-center py-16 border border-border/50 bg-card">
                <Ticket className="w-12 h-12 text-gold mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-xl mb-2">No Registrations Yet</h3>
                <p className="text-muted-foreground mb-6">Browse events and register to join the community.</p>
                <Button 
                  onClick={() => navigate('/events')}
                  className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-6 py-3 text-sm font-semibold rounded-sm"
                  data-testid="browse-events-dashboard-btn"
                >
                  Browse Events
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {myRsvps.map(({ event, type }, index) => (
                  <Link
                    key={event.id}
                    to={`/events/${event.id}`}
                    className="border border-border/50 bg-card p-6 flex flex-col md:flex-row md:items-center gap-4 hover:border-gold/50 transition-colors block"
                    data-testid={`my-registration-${index}`}
                  >
                    <div className="w-32 h-20 flex-shrink-0 overflow-hidden">
                      <img
                        src={event.image_url && !event.image_url.includes('example.com') ? event.image_url : 'https://images.pexels.com/photos/8761648/pexels-photo-8761648.jpeg'}
                        alt={event.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://images.pexels.com/photos/8761648/pexels-photo-8761648.jpeg'; }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="badge-category">
                          {getCategoryLabel(event.category)}
                        </span>
                        {type === 'ticket' && (
                          <span className="inline-flex items-center gap-1 text-xs bg-gold/20 text-gold px-2 py-0.5">
                            <Ticket className="w-3 h-3" /> Ticket
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl mb-2">{event.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(parseISO(event.date), 'MMM d, yyyy')} at {event.start_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Event Attendees Tab */}
          <TabsContent value="attendees" className="space-y-6">
            {!selectedEvent ? (
              <div className="text-center py-16 border border-border/50 bg-card">
                <Users className="w-12 h-12 text-gold mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-xl mb-2">Select an Event</h3>
                <p className="text-muted-foreground mb-6">
                  Go to "My Events" tab and click "Attendees" to view registrations.
                </p>
              </div>
            ) : (
              <>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border/50 pb-6">
                  <div>
                    <p className="text-muted-foreground text-sm mb-1">Attendees for</p>
                    <h2 className="text-2xl">{selectedEvent.title}</h2>
                    <p className="text-muted-foreground">
                      {format(parseISO(selectedEvent.date), 'MMMM d, yyyy')} • {selectedEventAttendees.total} attendee{selectedEventAttendees.total !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {isBusinessPremium() && (
                      <Button 
                        variant="outline"
                        onClick={() => openInviteDialog(selectedEvent)}
                        className="rounded-sm text-gold border-gold hover:bg-gold hover:text-primary uppercase tracking-widest"
                        data-testid="invite-from-attendees-btn"
                      >
                        <Mail className="w-4 h-4 mr-2" /> Invite Members
                      </Button>
                    )}
                    <Button 
                      onClick={() => downloadCsv(selectedEvent)}
                      className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-6 py-3 text-sm font-semibold rounded-sm"
                      data-testid="download-selected-csv-btn"
                    >
                      <Download className="w-4 h-4 mr-2" /> Download CSV
                    </Button>
                  </div>
                </div>

                {selectedEventAttendees.total === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No attendees yet for this event.</p>
                    {isBusinessPremium() && (
                      <Button 
                        variant="outline"
                        onClick={() => openInviteDialog(selectedEvent)}
                        className="mt-4 rounded-sm text-gold border-gold hover:bg-gold hover:text-primary"
                      >
                        <Mail className="w-4 h-4 mr-2" /> Send Invitations
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="border border-border/50 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/30">
                          <TableHead className="uppercase tracking-wider text-xs">Name</TableHead>
                          <TableHead className="uppercase tracking-wider text-xs">Email</TableHead>
                          <TableHead className="uppercase tracking-wider text-xs">Organization</TableHead>
                          <TableHead className="uppercase tracking-wider text-xs">Job Function</TableHead>
                          <TableHead className="uppercase tracking-wider text-xs">Type</TableHead>
                          <TableHead className="uppercase tracking-wider text-xs">Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedEventAttendees.rsvps.map((rsvp, index) => (
                          <TableRow key={`rsvp-${rsvp.id}`} data-testid={`rsvp-row-${index}`}>
                            <TableCell className="font-medium">{rsvp.user_name}</TableCell>
                            <TableCell>{rsvp.user_email}</TableCell>
                            <TableCell>{rsvp.user_organization || '-'}</TableCell>
                            <TableCell>{rsvp.user_job_function || '-'}</TableCell>
                            <TableCell>RSVP</TableCell>
                            <TableCell>{format(parseISO(rsvp.created_at), 'MMM d, yyyy')}</TableCell>
                          </TableRow>
                        ))}
                        {selectedEventAttendees.tickets.map((ticket, index) => (
                          <TableRow key={`ticket-${ticket.id}`} data-testid={`ticket-row-${index}`}>
                            <TableCell className="font-medium">{ticket.user_name}</TableCell>
                            <TableCell>{ticket.user_email}</TableCell>
                            <TableCell>{ticket.user_organization || '-'}</TableCell>
                            <TableCell>{ticket.user_job_function || '-'}</TableCell>
                            <TableCell>
                              <span className="inline-flex items-center gap-1 text-gold">
                                <Ticket className="w-3 h-3" /> Ticket
                              </span>
                            </TableCell>
                            <TableCell>{format(parseISO(ticket.created_at), 'MMM d, yyyy')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Invitation Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-gold" />
              Send Invitations
            </DialogTitle>
            <DialogDescription>
              Invite members matching your criteria to "{inviteEvent?.title}". 
              Members won't see each other's contact info - invitations are sent through the platform.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Target Job Functions */}
            <div>
              <Label className="text-sm uppercase tracking-wider mb-3 block flex items-center gap-2">
                <Target className="w-4 h-4 text-gold" />
                Target Job Functions (leave empty for all)
              </Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {jobFunctions.map((job) => (
                  <div key={job.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`invite-job-${job.value}`}
                      checked={inviteTargeting.target_job_functions.includes(job.value)}
                      onCheckedChange={(checked) => handleTargetingChange('target_job_functions', job.value, checked)}
                    />
                    <Label htmlFor={`invite-job-${job.value}`} className="text-sm cursor-pointer">
                      {job.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Target Company Types */}
            <div>
              <Label className="text-sm uppercase tracking-wider mb-3 block flex items-center gap-2">
                <Target className="w-4 h-4 text-gold" />
                Target Company Types (leave empty for all)
              </Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {companyTypes.map((type) => (
                  <div key={type.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`invite-type-${type.value}`}
                      checked={inviteTargeting.target_company_types.includes(type.value)}
                      onCheckedChange={(checked) => handleTargetingChange('target_company_types', type.value, checked)}
                    />
                    <Label htmlFor={`invite-type-${type.value}`} className="text-sm cursor-pointer">
                      {type.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Exclude Company Types */}
            <div>
              <Label className="text-sm uppercase tracking-wider mb-3 block text-destructive flex items-center gap-2">
                <Target className="w-4 h-4" />
                Exclude Company Types
              </Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {companyTypes.map((type) => (
                  <div key={type.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`exclude-type-${type.value}`}
                      checked={inviteTargeting.exclude_company_types.includes(type.value)}
                      onCheckedChange={(checked) => handleTargetingChange('exclude_company_types', type.value, checked)}
                    />
                    <Label htmlFor={`exclude-type-${type.value}`} className="text-sm cursor-pointer">
                      {type.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Message */}
            <div>
              <Label className="text-sm uppercase tracking-wider mb-2 block">
                Custom Message (Optional)
              </Label>
              <Textarea
                placeholder="Add a personal note to your invitation..."
                value={inviteTargeting.custom_message}
                onChange={(e) => setInviteTargeting(prev => ({ ...prev, custom_message: e.target.value }))}
                rows={3}
                className="rounded-sm"
              />
            </div>

            {/* Preview */}
            <div className="border border-border/50 p-4 bg-secondary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Matching Members</p>
                  <p className="text-2xl font-semibold">
                    {invitePreview ? invitePreview.matching_members : '—'}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={previewInvitations}
                  className="rounded-sm"
                >
                  Preview Count
                </Button>
              </div>
              {invitePreview && (
                <p className="text-xs text-muted-foreground mt-2">
                  {invitePreview.note}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendInvitations}
              disabled={sendingInvites || !invitePreview || invitePreview.matching_members === 0}
              className="bg-gold text-primary hover:bg-gold/90"
            >
              {sendingInvites ? (
                <span className="spinner mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send {invitePreview?.matching_members || 0} Invitations
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
